/**
 * sectionSlugs.ts — default `widget_name` per section TYPE, used as the FALLBACK
 * when an admin has not set a custom tracking name on the section card.
 *
 * Resolution order in NewPage.renderComponent:
 *   component.tracking_name  →  SECTION_SLUGS[section]   (NO generic fallback)
 *
 * DEV DUTY — every section type MUST have a default name here. `SECTION_SLUGS` is
 * typed `Record<SectionCode, string>`, so if a section code is missing its slug
 * the build FAILS ("Property '<code>' is missing"). There is intentionally NO
 * `section_<code>` runtime fallback: a section without a default name is a
 * compile error, not a silently-generated name. When you add a new section type
 * to constants.js SECTION_TYPES, add its code to `SectionCode` AND its slug here.
 *
 * IMPORT-FREE ON PURPOSE (same rule as widgetNames.ts): keyed by the RAW numeric
 * section codes from `constants.js` SECTION_TYPES as plain strings, so this file
 * pulls in NOTHING — referencing it from public widgets can never drag the admin
 * widget graph into the public bundle. `SectionCode` mirrors SECTION_TYPES here
 * rather than importing it, to keep that guarantee.
 *
 * Slugs follow the tracking sheet where a canonical name exists (1→top_banner,
 * 4→about_us, 40→course_outcome); the rest are sensible snake_case defaults.
 * Part of the decoupled analytics feature — delete alongside rudderStackEvents.ts.
 */

/**
 * Every section code that can be rendered (mirror of constants.js SECTION_TYPES
 * values). Adding a code here forces a matching SECTION_SLUGS entry — that is the
 * compile-time guarantee that every section has a default name.
 */
export type SectionCode =
  | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10"
  | "11" | "12" | "13" | "14" | "15" | "16" | "17" | "18" | "19" | "20"
  | "21" | "22" | "23" | "24" | "25" | "26" | "27" | "28" | "29" | "30"
  | "31" | "32" | "33" | "34" | "35" | "36" | "37" | "38" | "39" | "40"
  | "41" | "42" | "43" | "44" | "45" | "46" | "47" | "48" | "49" | "50"
  | "51" | "52" | "53" | "54" | "55" | "56" | "57" | "58" | "59" | "60"
  | "61" | "62" | "63" | "64" | "3000" | "3001" | "3002" | "3003" | "3004" | "3005" | "3006"
  | "3007"
  | "7000" | "7001" | "7002" | "7003";

/**
 * Default widget_name for every section type. Exhaustive by construction: a
 * missing key is a compile error (see DEV DUTY above).
 */
export const SECTION_SLUGS: Record<SectionCode, string> = {
  "1": "top_banner",
  "2": "header",
  "3": "menu_items",
  "4": "about_us",
  "5": "key_numbers",
  "6": "accreditation",
  "7": "academic_deliverables",
  "8": "scholar",
  "9": "recruiters",
  "10": "vice_chancellor",
  "11": "faculty",
  "12": "testimonials",
  "13": "masterclass",
  "14": "message",
  "15": "placements",
  "16": "mobile_app",
  "17": "faq",
  "18": "footer",
  "19": "subscribe",
  "20": "program_cards",
  "21": "specialisation",
  "22": "career_prospects",
  "23": "success_stories",
  "24": "what_makes_course_different",
  "25": "eligibility",
  "26": "thank_you",
  "27": "referral_form",
  "28": "influencer_form",
  "29": "program_structure",
  "30": "elective",
  "31": "program_fee",
  "32": "rich_text_editor",
  "33": "mobile_lead_form",
  "34": "counseling",
  "35": "program_prospects",
  "36": "course_specialisations",
  "37": "examination_guidelines",
  "38": "live_lecture",
  "39": "program_benefits",
  "40": "course_outcome",
  "41": "certificates",
  "42": "application_process",
  "43": "skills",
  "44": "who_is_this_program_for",
  "45": "banner_video",
  "46": "comparison",
  "47": "leaders",
  "48": "digital_learning",
  "49": "visionaries",
  "50": "para_text",
  "51": "blog_with_lead",
  "52": "latest_blogs",
  "53": "counselor_call_cta",
  "54": "breadcrumb_banner",
  "55": "blog_list",
  "56": "blog_inline_image",
  "57": "popular_blogs",
  "58": "learn_from_leaders",
  "59": "case_study",
  "60": "brand",
  "61": "institutes",
  "62": "walk_through",
  "63": "life_milestone",
  "64": "enrollments",
  "3000": "grievance_form",
  "3001": "heading",
  "3002": "author_profile",
  "3003": "author_card",
  "3004": "author_byline",
  "3005": "specialisation_carousel",
  "3006": "skill_cards",
  "3007": "mooc_form",
  "7000": "internship_heading",
  "7001": "internship_about",
  "7002": "internship_registration_process",
  "7003": "internship_courses",
};

/**
 * Resolve the widget_name for a section instance: admin-provided name wins, else
 * the per-type default. No generic fallback — every section code is guaranteed a
 * default by SECTION_SLUGS's exhaustive typing, so the result is always a real,
 * intentional name.
 */
export const resolveWidgetName = (
  section: SectionCode | string,
  trackingName?: string | null,
): string => {
  const custom = typeof trackingName === "string" ? trackingName.trim() : "";
  if (custom) return custom;
  return SECTION_SLUGS[section as SectionCode];
};
